"use client";

import Image from "next/image";
import { ChevronLeft, ChevronRight, Trophy } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";

type ApiEnvelope<T> = {
  success: boolean;
  data?: T;
};

type HomePageScoreReport = {
  id: string;
  imageUrl: string;
  alt: string;
};

type ScoreReportImagesResponse = {
  images: Array<{
    id: string;
    imageUrl: string;
  }>;
};

const fallbackScoreReports: HomePageScoreReport[] = [
  {
    id: "report-1",
    imageUrl: "/assets/home-page-10/reports/report1.webp",
    alt: "PTE Ultra student exam score report 1",
  },
  {
    id: "report-2",
    imageUrl: "/assets/home-page-10/reports/report2.webp",
    alt: "PTE Ultra student exam score report 2",
  },
  {
    id: "report-3",
    imageUrl: "/assets/home-page-10/reports/report3.webp",
    alt: "PTE Ultra student exam score report 3",
  },
  {
    id: "report-4",
    imageUrl: "/assets/home-page-10/reports/report4.webp",
    alt: "PTE Ultra student exam score report 4",
  },
  {
    id: "report-5",
    imageUrl: "/assets/home-page-10/reports/report5.webp",
    alt: "PTE Ultra student exam score report 5",
  },
];

type ExamScoreReportsCarouselProps = {
  apiUrl: string;
};

function isHttpUrl(value: string) {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

export default function ExamScoreReportsCarousel({
  apiUrl,
}: ExamScoreReportsCarouselProps) {
  const [scoreReports, setScoreReports] =
    useState<HomePageScoreReport[]>(fallbackScoreReports);
  const [activeSlide, setActiveSlide] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    dragFree: false,
    loop: true,
  });

  const updateActiveSlide = useCallback(() => {
    if (!emblaApi) return;
    setActiveSlide(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    const normalizedApiUrl = apiUrl.trim().replace(/\/+$/, "");
    if (!normalizedApiUrl) return;

    const controller = new AbortController();

    async function fetchScoreReports() {
      try {
        const response = await fetch(
          `${normalizedApiUrl}/HomePageContents/score-reports`,
          { signal: controller.signal },
        );
        if (!response.ok) return;

        const body =
          (await response.json()) as ApiEnvelope<ScoreReportImagesResponse>;
        if (!body.success || !Array.isArray(body.data?.images)) return;

        const reports = body.data.images
          .filter(
            (image) =>
              image?.id &&
              image.imageUrl?.trim() &&
              isHttpUrl(image.imageUrl.trim()),
          )
          .map((image, index) => ({
            id: image.id,
            imageUrl: image.imageUrl.trim(),
            alt: `PTE Ultra student exam score report ${index + 1}`,
          }));

        setScoreReports(reports);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError")
          return;
      }
    }

    void fetchScoreReports();
    return () => controller.abort();
  }, [apiUrl]);

  useEffect(() => {
    if (!emblaApi) return;

    emblaApi.on("select", updateActiveSlide);
    emblaApi.on("reInit", updateActiveSlide);

    return () => {
      emblaApi.off("select", updateActiveSlide);
      emblaApi.off("reInit", updateActiveSlide);
    };
  }, [emblaApi, updateActiveSlide]);

  if (scoreReports.length === 0) return null;

  return (
    <section
      aria-label="Student exam score reports"
      className="mt-14 border-t border-stone-950/10 pt-12 sm:mt-16 sm:pt-14"
    >
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-3xl">
          <div className="secondary-tint-chip inline-flex items-center gap-2 rounded-full px-4 py-2 text-[0.86rem] font-semibold text-stone-600">
            <Trophy className="size-4 text-secondary" />
            <span>Student score reports</span>
          </div>
          <h2 className="mt-5 text-[2.15rem] font-semibold leading-tight tracking-tight text-stone-950 sm:text-[2.85rem] lg:text-[3.35rem]">
            Score cards from students who turned practice into results
          </h2>
          <p className="mt-4 max-w-[62ch] text-[1rem] leading-8 text-stone-600 sm:text-[1.08rem]">
            These exam reports show what focused PTE preparation can look like
            when learners keep improving with every mock test.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="Show previous score report"
            onClick={() => emblaApi?.scrollPrev()}
            className="grid size-11 place-items-center rounded-full border border-stone-950/10 bg-white/80 text-stone-950 shadow-[0_12px_28px_rgba(15,23,42,0.12)] backdrop-blur transition hover:border-secondary/30 hover:text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60"
          >
            <ChevronLeft className="size-5" />
          </button>
          <button
            type="button"
            aria-label="Show next score report"
            onClick={() => emblaApi?.scrollNext()}
            className="grid size-11 place-items-center rounded-full border border-stone-950/10 bg-white/80 text-stone-950 shadow-[0_12px_28px_rgba(15,23,42,0.12)] backdrop-blur transition hover:border-secondary/30 hover:text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60"
          >
            <ChevronRight className="size-5" />
          </button>
        </div>
      </div>

      <div ref={emblaRef} className="mt-9 overflow-hidden">
        <div className="-ml-4 flex touch-pan-y sm:-ml-5 lg:-ml-6">
          {scoreReports.map((report, index) => (
            <div
              key={report.id}
              className="min-w-0 flex-[0_0_88%] pl-4 sm:flex-[0_0_54%] sm:pl-5 lg:flex-[0_0_34%] lg:pl-6"
            >
              <article className="overflow-hidden rounded-lg border border-stone-950/10 bg-white p-2 shadow-[0_22px_70px_rgba(15,23,42,0.14)] sm:p-3">
                <Image
                  src={report.imageUrl}
                  alt={report.alt}
                  width={1229}
                  height={1280}
                  priority={index === 0}
                  className="h-auto w-full rounded-md object-contain"
                  sizes="(min-width: 1024px) 31vw, (min-width: 640px) 50vw, 88vw"
                />
              </article>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 flex justify-center gap-2">
        {scoreReports.map((report, index) => (
          <button
            key={`${report.id}-indicator`}
            type="button"
            aria-label={`Show score report ${index + 1}`}
            aria-current={activeSlide === index ? "true" : undefined}
            onClick={() => emblaApi?.scrollTo(index)}
            className={`h-2.5 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 ${
              activeSlide === index
                ? "w-8 bg-secondary"
                : "w-2.5 bg-stone-950/22 hover:bg-stone-950/42"
            }`}
          />
        ))}
      </div>
    </section>
  );
}
